<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'

import { DashboardWidget, WidgetType, OngoingReport } from '@/types/dashboard'
import { uuid } from '@/types/general'
import { useOngoingReportStore } from '@/stores/ongoing-driver-report-store'
import { datetimeShort, differenceBetweenDates, differenceFromMinutes } from '@/utils/dates'
import { useOngoingReportWidgetData } from '@/hooks/driver-reports/use-ongoing-driver-report-widget-data'
import { EventType, TourType } from '@/types/driver-report'

import MyWidget from '@/components/dashboard/widgets/MyWidget.vue'
import ReportModal from '@/components/driver-reports/ReportModal.vue'

interface Props {
    widget: DashboardWidget<WidgetType.DriverReportsOngoingReportList>
    preview?: boolean
}

interface PreparedOngoingReport extends OngoingReport {
    startTime: string
    units: string
    duration: string
    customerStopCount: number
    kilometerCount: number
    waitTime: string
    totalStopTime: string
}

const props = defineProps<Props>()

const { t } = useI18n()
const ongoingReportStore = useOngoingReportStore()
const ongoingReportData = useOngoingReportWidgetData()
const selectedReportId = ref<uuid>()

const reports = computed<PreparedOngoingReport[]>(() => {
    return ongoingReportData.reports.value.map((report) => {
        return {
            ...report,
            startTime: datetimeShort(report.startedAt),
            duration: differenceBetweenDates(report.startedAt, new Date().toDateString()),
            units: report.events
                .map((e) => e.units.map((unit) => unit.registrationNumber))
                .flat()
                .join(', '),
            customerStopCount: report.events.filter((e) => e.type === EventType.Customer).length,
            kilometerCount: report.events.at(-1)!.kilometerCount - report.events[0].kilometerCount,
            waitTime: differenceFromMinutes(
                report.events.map((e) => e.waitTime).reduce((a, b) => a + b, 0),
            ),
            // Get the total stop time (difference between startedAt and endedAt or now) from all events
            totalStopTime: differenceFromMinutes(
                report.events
                    .map((event) =>
                        Math.abs(
                            dayjs(event.startedAt).diff(
                                event.endedAt ?? new Date().toDateString(),
                                'minutes',
                                true,
                            ),
                        ),
                    )
                    .reduce((a, b) => a + b, 0),
            ),
        }
    })
})

function showReport(report: OngoingReport) {
    selectedReportId.value = report.id
}

function toggleReportSelection(report: PreparedOngoingReport) {
    if (ongoingReportStore.selectedReportId === report.id) {
        ongoingReportStore.resetSelectedReport()
    } else {
        ongoingReportStore.setSelectedReport(report)
    }
}

watch(() => props.widget.options, ongoingReportData.fetchOngoingReports)
</script>

<template>
    <MyWidget :widget="props.widget" :preview="props.preview">
        <div class="flex flex-col">
            <div
                v-for="(report, index) in reports"
                :key="report.id"
                class="p-5 transition-colors group flex justify-between w-full"
                :class="{
                    'bg-primary-300 dark:bg-primary-500':
                        ongoingReportStore.highlightedReportId === report.id ||
                        ongoingReportStore.selectedReportId === report.id,
                }"
                @mouseenter="ongoingReportStore.setHighlightedReport(report)"
                @mouseleave="ongoingReportStore.resetHighlightedReport()"
                @click="toggleReportSelection(report)"
            >
                <div class="flex flex-col w-max">
                    <div class="flex items-center space-x-1">
                        <div
                            class="aspect-square flex-shrink-0 h-8 flex justify-center items-center rounded-full border-4 border-green-500 font-semibold z-10"
                            v-text="index + 1"
                        />

                        <div class="flex items-center w-full">
                            <span class="font-semibold" v-text="report.referenceNumber" />
                            <span
                                v-if="report.referenceNumber"
                                class="ml-1 font-semibold"
                                v-text="'-'"
                            />
                            <span class="ml-1 font-semibold" v-text="TourType[report.tourType]" />

                            <mdi:eye
                                v-tooltip="t('showReport')"
                                class="invisible group-hover:visible ml-2 cursor-pointer hover:text-primary-400 dark:hover:text-primary-300 min-h-5 min-w-5 text-base"
                                @click="showReport(report)"
                            />
                        </div>
                    </div>
                    <div class="flex flex-col w-full mt-1">
                        <div class="flex items-center w-full">
                            <mdi:truck v-if="report.vehicleRegistrationNumber" class="mr-1" />
                            <span class="text-xs" v-text="report.vehicleRegistrationNumber" />
                            <mdi:truck-trailer v-if="report.units" class="mr-1 ml-2" />
                            <span class="text-xs" v-text="report.units" />
                        </div>
                    </div>
                    <div class="flex flex-col">
                        <div class="flex items-center space-x-1.5">
                            <span class="font-semibold text-xs" v-text="t('startTime')" />
                            <span class="text-xs" v-text="report.startTime" />
                        </div>

                        <div class="flex items-center space-x-1.5">
                            <span class="font-semibold text-xs" v-text="t('duration')" />
                            <span
                                v-tooltip="t('hourMinuteFormatExplanation')"
                                class="text-xs"
                                v-text="report.duration"
                            />
                        </div>

                        <div class="flex items-center space-x-1.5">
                            <span class="font-semibold text-xs" v-text="t('kilometerCount')" />
                            <span class="text-xs" v-text="report.kilometerCount" />
                        </div>
                    </div>
                </div>

                <div class="flex flex-col items-end w-max">
                    <div class="flex items-center space-x-1">
                        <mdi:account />
                        <span class="flex-shrink-0" v-text="report.userName" />
                    </div>
                    <div class="flex items-center space-x-1.5">
                        <span class="font-semibold text-xs" v-text="t('customerStops')" />
                        <span class="text-xs" v-text="report.customerStopCount" />
                    </div>

                    <div class="flex items-center space-x-1.5">
                        <span class="font-semibold text-xs" v-text="t('totalStopTime')" />
                        <span class="text-xs" v-text="report.totalStopTime" />
                    </div>

                    <div class="flex items-center space-x-1.5">
                        <span class="font-semibold text-xs" v-text="t('waitTime')" />
                        <span class="text-xs" v-text="report.waitTime" />
                    </div>
                    <div class="flex items-center space-x-1.5">
                        <span class="font-semibold text-xs" v-text="t('restingTime')" />
                        <span class="text-xs" v-text="differenceFromMinutes(report.restingTime)" />
                    </div>
                </div>
            </div>
        </div>

        <div
            v-if="reports.length === 0 && !ongoingReportData.loading.value"
            class="absolute right-0 top-0 flex justify-center items-center text-center w-full h-full text-white bg-black/80 text-2xl uppercase"
            v-text="t('noOngoingReports')"
        />

        <ReportModal
            v-if="selectedReportId"
            :id="selectedReportId"
            @update:open="selectedReportId = undefined"
        />
    </MyWidget>
</template>
